import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Plus, Trash2, Tag, X, Search, Pencil,
  CheckSquare, Square, Tags, FolderKanban, ChevronRight, MoveRight,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { keywordGroupsApi, type KeywordGroupDetail as GroupDetail, type KeywordItem } from "@/api";

export default function KeywordGroupDetail() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("__all__");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [newKeywords, setNewKeywords] = useState("");
  const [newTags, setNewTags] = useState("");
  const [editItem, setEditItem] = useState<KeywordItem | null>(null);
  const [editKeyword, setEditKeyword] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [batchTagInput, setBatchTagInput] = useState("");
  const [moveTarget, setMoveTarget] = useState("__none__");

  // Data
  const { data: group, isLoading } = useQuery<GroupDetail>({
    queryKey: ["keyword-group", groupId],
    queryFn: async () => (await keywordGroupsApi.get(groupId!)).data!,
    enabled: !!groupId,
  });

  const items = group?.items || [];
  const availableTags = group?.available_tags || [];
  const siblingGroups = (group?.sibling_groups || []).filter(g => g.group_id !== groupId);

  // Filter
  const filtered = useMemo(() => {
    let result = items;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i => i.keyword.toLowerCase().includes(q));
    }
    if (filterTag !== "__all__") {
      result = result.filter(i => i.tags?.includes(filterTag));
    }
    return result;
  }, [items, search, filterTag]);

  const allSelected = filtered.length > 0 && filtered.every(i => selected.has(i.item_id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.item_id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["keyword-group", groupId] });

  // Mutations
  const addMutation = useMutation({
    mutationFn: () => {
      const keywords = newKeywords.split("\n").map(k => k.trim()).filter(Boolean);
      const tags = newTags.split(",").map(t => t.trim()).filter(Boolean);
      return keywordGroupsApi.addItems(groupId!, { keywords, tags: tags.length ? tags : undefined });
    },
    onSuccess: (res) => {
      invalidate();
      toast.success(`已新增 ${res.data?.created || 1} 個關鍵字`);
      setNewKeywords(""); setNewTags(""); setAddOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editItem) return Promise.resolve();
      const tags = editTags.split(",").map(t => t.trim()).filter(Boolean);
      return keywordGroupsApi.updateItem(groupId!, editItem.item_id, {
        keyword: editKeyword.trim(),
        tags,
        notes: editNotes.trim() || undefined,
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success("已更新");
      setEditItem(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => keywordGroupsApi.deleteItem(groupId!, itemId),
    onSuccess: () => { invalidate(); toast.success("已刪除"); },
  });

  const batchMutation = useMutation({
    mutationFn: (params: { action: 'add_tag' | 'remove_tag' | 'delete' | 'move'; tag?: string; target_group_id?: string }) =>
      keywordGroupsApi.batch(groupId!, { action: params.action, item_ids: Array.from(selected), tag: params.tag, target_group_id: params.target_group_id }),
    onSuccess: (_, params) => {
      invalidate();
      setSelected(new Set());
      setMoveTarget("__none__");
      const msgs: Record<string, string> = { add_tag: '已加 Tag', remove_tag: '已移除 Tag', delete: '已刪除', move: '已移動到目標群組' };
      toast.success(msgs[params.action] || '完成');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const openEdit = (item: KeywordItem) => {
    setEditItem(item);
    setEditKeyword(item.keyword);
    setEditTags(item.tags?.join(", ") || "");
    setEditNotes(item.notes || "");
  };

  if (isLoading) return <div className="p-8 text-center text-muted-foreground">載入中...</div>;
  if (!group) return <div className="p-8 text-center text-muted-foreground">找不到群組</div>;

  return (
    <div className="space-y-4">
      {/* Breadcrumb navigation */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <button className="hover:text-foreground transition-colors flex items-center gap-1" onClick={() => navigate("/projects")}>
            <FolderKanban className="h-3.5 w-3.5" />專案管理
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <button className="hover:text-foreground transition-colors" onClick={() => navigate(`/keyword-groups?project=${group.project_id}`)}>
            {group.project_name || '專案'}
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">{group.name}</span>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/keyword-groups?project=${group.project_id}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Tags className="h-6 w-6 text-primary" />{group.name}
            </h1>
            <p className="text-sm text-muted-foreground">{group.keyword_count} 個關鍵字{group.description ? ` — ${group.description}` : ""}</p>
          </div>
        </div>
      </div>

      {/* Toolbar: search + filter + add */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="搜尋關鍵字..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">全部 Tag</SelectItem>
                {availableTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => { setNewKeywords(""); setNewTags(""); setAddOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" />新增關鍵字
            </Button>
          </div>

          {/* Batch actions bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 mt-3 p-2 bg-muted rounded-md">
              <span className="text-sm font-medium">已選 {selected.size} 項</span>
              <div className="flex items-center gap-1 ml-auto">
                <Input
                  className="w-[140px] h-8 text-sm"
                  placeholder="輸入 Tag..."
                  value={batchTagInput}
                  onChange={e => setBatchTagInput(e.target.value)}
                />
                <Button size="sm" variant="outline" disabled={!batchTagInput.trim()}
                  onClick={() => { batchMutation.mutate({ action: 'add_tag', tag: batchTagInput.trim() }); setBatchTagInput(""); }}>
                  <Tag className="h-3.5 w-3.5 mr-1" />加 Tag
                </Button>
                <Button size="sm" variant="outline" disabled={!batchTagInput.trim()}
                  onClick={() => { batchMutation.mutate({ action: 'remove_tag', tag: batchTagInput.trim() }); setBatchTagInput(""); }}>
                  <X className="h-3.5 w-3.5 mr-1" />移除 Tag
                </Button>
                {siblingGroups.length > 0 && (
                  <>
                    <span className="text-muted-foreground mx-1">|</span>
                    <Select value={moveTarget} onValueChange={setMoveTarget}>
                      <SelectTrigger className="w-[160px] h-8 text-sm">
                        <SelectValue placeholder="移動到..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">移動到...</SelectItem>
                        {siblingGroups.map(g => (
                          <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" disabled={moveTarget === "__none__"}
                      onClick={() => { if (confirm(`確定移動 ${selected.size} 個關鍵字？`)) batchMutation.mutate({ action: 'move', target_group_id: moveTarget }); }}>
                      <MoveRight className="h-3.5 w-3.5 mr-1" />移動
                    </Button>
                  </>
                )}
                <span className="text-muted-foreground mx-1">|</span>
                <Button size="sm" variant="destructive"
                  onClick={() => { if (confirm(`確定刪除 ${selected.size} 個關鍵字？`)) batchMutation.mutate({ action: 'delete' }); }}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />刪除
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Keywords table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead>關鍵字</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="w-[80px]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    {items.length === 0 ? "尚無關鍵字" : "無符合條件的關鍵字"}
                  </TableCell>
                </TableRow>
              ) : filtered.map(item => (
                <TableRow key={item.item_id}>
                  <TableCell>
                    <Checkbox checked={selected.has(item.item_id)} onCheckedChange={() => toggleOne(item.item_id)} />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{item.keyword}</span>
                    {item.notes && <p className="text-xs text-muted-foreground mt-0.5">{item.notes}</p>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {item.tags?.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={() => { if (confirm("確定刪除？")) deleteMutation.mutate(item.item_id); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-right">
        顯示 {filtered.length} / {items.length} 筆
      </p>

      {/* Add keywords dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>新增關鍵字</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>關鍵字（每行一個）</Label>
              <Textarea className="min-h-[150px] font-mono text-sm" value={newKeywords} onChange={e => setNewKeywords(e.target.value)}
                placeholder={"blue disposable isolation gowns\nisolation gown types\nbuy medical isolation gown"} />
              <p className="text-xs text-muted-foreground mt-1">
                {newKeywords.split("\n").filter(k => k.trim()).length} 個關鍵字
              </p>
            </div>
            <div>
              <Label>Tags（逗號分隔，選填）</Label>
              <Input value={newTags} onChange={e => setNewTags(e.target.value)} placeholder="購買, 隔離衣, 品牌" />
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs text-muted-foreground mr-1">已有 Tag:</span>
                  {availableTags.map(t => (
                    <Badge key={t} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10"
                      onClick={() => {
                        const current = newTags.split(",").map(s => s.trim()).filter(Boolean);
                        if (!current.includes(t)) setNewTags([...current, t].join(", "));
                      }}>
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => addMutation.mutate()} disabled={!newKeywords.trim() || addMutation.isPending}>
              {addMutation.isPending ? "新增中..." : "新增"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit keyword dialog */}
      <Dialog open={!!editItem} onOpenChange={open => { if (!open) setEditItem(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>編輯關鍵字</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>關鍵字</Label>
              <Input value={editKeyword} onChange={e => setEditKeyword(e.target.value)} />
            </div>
            <div>
              <Label>Tags（逗號分隔）</Label>
              <Input value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="購買, 隔離衣" />
              {availableTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {availableTags.map(t => (
                    <Badge key={t} variant="outline" className="text-xs cursor-pointer hover:bg-primary/10"
                      onClick={() => {
                        const current = editTags.split(",").map(s => s.trim()).filter(Boolean);
                        if (!current.includes(t)) setEditTags([...current, t].join(", "));
                      }}>
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>備註（選填）</Label>
              <Input value={editNotes} onChange={e => setEditNotes(e.target.value)} />
            </div>
            <Button onClick={() => updateMutation.mutate()} disabled={!editKeyword.trim() || updateMutation.isPending}>
              {updateMutation.isPending ? "儲存中..." : "儲存"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
